import React, {useEffect, useMemo, useState} from "react";

import Box from '@mui/material/Box';
import Stack from "@mui/material/Stack";

import {PageHeader} from "./PageHeader";
import {useAppCtx} from "../app/AppCtx";
import {useCodeCtx} from "../app/CodeCtx";
import {useCrewCtx} from "../app/CrewCtx";
import {accessCtrl, canManageTaskInList} from "../ctx/AccessCtrl"
import {CxTextButton, CxTooltipButton, CxTooltipTextButton} from "../cmx/CxButton";
import {CxCapa, CxToolCapa} from "../cmx/CxCapa"
import {CxAppColor} from "../cmx/CxColor";
import {CxInfoDialog} from "../cmx/CxDialog";
import {CxListFeedback} from "../cmx/CxFeedback";
import {CxBox, CxComboxSelection, CxLocalization, CxDateInput} from "../cmx/CxForm";
import {CxDataGrid, CxDataTable} from "../cmx/CxData";
import {CxGrid} from "../cmx/CxGrid";
import {CxSwitch} from "../cmx/CxSelection"
import {CxSubtitle, CxSection} from "../cmx/CxTypography"
import {CxReportDialog, CxGroupReportDialog} from "../cmx/CxReport"
import {errorStatus, pendingStatus, successStatus} from "../ctx/AppStatus";
import {Task} from "../domain/Task";
import {Capa} from "../domain/Capa";
import {Report} from "../domain/Report";
import {runningCall, successCall} from "../gateway/CallService";
import {taskListRange} from "../gateway/TaskService";
import {CalendarUtil} from "../lib/CalendarUtil"
import {DateUtil} from "../lib/DateUtil"
import {JsonUtil} from "../lib/JsonUtil"

export function TaskList() {
    const {handleAction, getPageState, setPageState, updateAppStatus} = useAppCtx();
    const {code, lazyloadCodes} = useCodeCtx();
    const {crew, lazyloadCrews} = useCrewCtx();

    useEffect(() => {
        lazyloadCodes();
        lazyloadCrews();
    }, []);

    // ----------------------------------------------------------------------------------------------------------------
    // header and view handling
    // ----------------------------------------------------------------------------------------------------------------

    const title = "Planung";

    const view = getPageState('TaskList', 'view');
    const deputy = getPageState('TaskList', 'deputy');
    const member = getPageState('TaskList', 'member');
    const capa = getPageState('TaskList', 'capa');

    const [toolCapa, setToolCapa] = useState(false)             // values: [true, false]
    const [report, setReport] = useState(null)                          // values: [null | 'material' | 'clearing']

    const handleViewChange = (value) => {
        setPageState('TaskList', 'view', value);
    };

    const handleDeputyViewChange = (value) => {
        setPageState('TaskList', 'deputy', value);
    }

    const handleMemberViewChange = (value) => {
        setPageState('TaskList', 'member', value);
    }

    const handleCapaChange = () => {
        setPageState('TaskList', 'capa', !capa);
    }

    const handleLocalActionChange = (value) => {
        if (value === "openToolCapa") {
            setToolCapa(true);
        } else if (value === "openMaterialReport") {
            setReport(Report.MATERIAL);
        } else if (value === "openClearingReport") {
            setReport(Report.CLEARING);
        } else if (value === "openPlanReport") {
            if (displayOption === 0) {
                setReport(Report.PLAN_BY_TASK)
            } else if (displayOption === 1) {
                setReport(Report.PLAN_BY_TEAM)
            } else {
                setReport(Report.PLAN)
            }
        } else if (value === "openToolReport") {
            setReport(Report.TOOL);
        }
    };

    const handleInfoChange = (value) => {
        if (value === "close") {
            setToolCapa(false);
        }
    }

    const viewItems = [
        { label: "Woche", value: "weekView" },
        { label: "Tag",   value: "dayView"  },
        { label: "Datum", value: "dateView" }
    ];

    const localActionItems= [
        { kind: "tooltip", label: "Planungsliste",      iconName: "plan",     actionName: "openPlanReport"     },
        { kind: "tooltip", label: "Bestellliste",       iconName: "material", actionName: "openMaterialReport" },
        { kind: "tooltip", label: "Abräumliste",        iconName: "cleaning", actionName: "openClearingReport" },
        { kind: "tooltip", label: "Werkzeugliste",      iconName: "tool",     actionName: "openToolReport"     },
        { kind: "tooltip", label: "Werkzeugauslastung", iconName: "capa",     actionName: "openToolCapa"       }
    ]

    const actionItems = [
        { kind: "tooltip", label: "Arbeitsvorgänge suchen",iconName: "search", actionName: "taskSearch" }
    ];

    const renderHeader = () => {
        return (
            <PageHeader
                title={title}
                viewItems={viewItems}
                viewInitialValue={view}
                viewOnChangeHandler={handleViewChange}
                localActionItems={localActionItems}
                localActionOnChangeHandler={handleLocalActionChange}
                actionItems={actionItems}
            />
        );
    }

    const renderSubHeader = () => {
        const isDayView = view === "dayView" || view === "dateView";

        return (
            <CxGrid container direction="row" spacing={1} alignItems="center" sx={{mt: 2, mb: 2}}>
                <CxGrid size="md">

                    <CxGrid size="xl" container spacing={2}>
                        <CxGrid size="lg">
                            <CxSwitch name="deputy" label="Stellvertreter" checked={deputy}
                                      onChange={handleDeputyViewChange} disabled={!accessCtrl.canCtrlTaskList} />
                        </CxGrid>
                        <CxGrid size="lg">
                            <CxSwitch name="member" label="Mitarbeiter" checked={member}
                                      onChange={handleMemberViewChange} disabled={!accessCtrl.canCtrlTaskList} />
                        </CxGrid>
                    </CxGrid>

                </CxGrid>

                <CxGrid size="md" align="center">
                    { renderCtrlSubtitle(isDayView) }
                </CxGrid>

                <CxGrid size="md" align="right" sx={{gap: 2}}>
                    &nbsp;
                </CxGrid>
            </CxGrid>
        );
    }

    // ----------------------------------------------------------------------------------------------------------------
    // control panel
    // ----------------------------------------------------------------------------------------------------------------

    const week          = getPageState('TaskList', 'week');
    const day           = getPageState('TaskList', 'day');
    const dayIndex      = getPageState('TaskList', 'dayIndex');

    const foremanFilter = getPageState('TaskList', 'foremanFilter');
    const groupFilter   = getPageState('TaskList', 'groupFilter');
    const memberFilter  = getPageState('TaskList', 'memberFilter');
    const taskFilter    = getPageState('TaskList', 'taskFilter');
    const displayOption = getPageState('TaskList', 'displayOption');

    const handleWeekChange = (week) => {
        setPageState('TaskList', 'week', week);
        setPageState('TaskList', 'day',  week['days'][week.initialDayIdx]);
        setPageState('TaskList', 'dayIndex', week.initialDayIdx);
    };

    const handleDayChange = (day, index) => {
        setPageState('TaskList', 'day',  day);
        setPageState('TaskList', 'dayIndex', index);
    }

    const handleDateChange = (value) => {
        const day = value
        const week = CalendarUtil.isoWeek(day);
        const dayIndex  = week.daysIso.indexOf(DateUtil.convertDateToIsoString(day));
        setPageState('TaskList', 'week', week);
        setPageState('TaskList', 'day',  day);
        setPageState('TaskList', 'dayIndex', dayIndex);
    }

    const handleCapaViewChange = (capaView) => {
        setPageState('TaskList', 'showNumbers', capaView !== 0);
    };

    const handleForemanChange = (value) => {
        setPageState('TaskList', 'foremanFilter', value);
    };

    const handleGroupChange = (value) => {
        setPageState('TaskList', 'groupFilter', value);
    }

    const handleMemberChange = (value) => {
        setPageState('TaskList', 'memberFilter', value);
    }

    const handleTaskChange = (value) => {
        setPageState('TaskList', 'taskFilter', value);
    };

    const handleDisplayChange = (value) => {
        setPageState('TaskList', 'displayOption', value);
    };

    const dayLabel = (day, dayIndex) => {
        const dayString= DateUtil.convertDateToString(day)
        const dayName = CalendarUtil.dayName(dayIndex);
        return `${dayName} ${dayString}`;
    }

    const weekLabel = (w) => {
        return `${w['startLabel']} - ${w['endLabel']}`;
    }

    const kwLabel = (w) => {
        return `KW${w['nr']}`;
    }

    const kwWeekLabel = (w) => {
        return `KW${w['nr']} (${w['startLabel']} - ${w['endLabel']})`;
    }

    //
    // render control parts
    //
    const renderCtrlSubtitle = (isDayView) => {
        const selection = isDayView ? `${dayLabel(day, dayIndex)}` : `${weekLabel(week)}`;

        const color = isDayView ? (dayIndex === 5 ? CxAppColor.saturday
                : dayIndex === 6 ? CxAppColor.sunday : CxAppColor.weekday) : CxAppColor.weekday;

        return (
            <CxSubtitle sx={{ color }}>{selection}</CxSubtitle>
        );
    }

    const renderCtrlWeek = (p2Week, p1Week, n1Week, n2Week) => {
        const sxOptions = {minWidth: '10px', height: '40px', padding: '3px 3px'};
        return (
            <>
                <CxTooltipTextButton key="1" label={kwLabel(p2Week)}  tooltip={kwWeekLabel(p2Week)}
                                     onClick={() => handleWeekChange(p2Week)} sx={sxOptions} />

                <CxTooltipTextButton key="2" label={kwLabel(p1Week)}  tooltip={kwWeekLabel(p1Week)}
                                     onClick={() => handleWeekChange(p1Week)} sx={sxOptions} />

                <CxTextButton        key="3" label={kwLabel(week)}    variant="outlined" sx={sxOptions} />

                <CxTooltipTextButton key="4" label={kwLabel(n1Week)}  tooltip={kwWeekLabel(n1Week)}
                                     onClick={() => handleWeekChange(n1Week)} sx={sxOptions} />

                <CxTooltipTextButton key="5" label={kwLabel(n2Week)}  tooltip={kwWeekLabel(n2Week)}
                                     onClick={() => handleWeekChange(n2Week)} sx={sxOptions} />
            </>
        );
    }

    const renderCtrlDays = () => {
        return (
            <>
                {week['daysDayLabel'].map((label, index) => (
                    <CxTextButton key={index} label={label}
                        variant={day === week['days'][index] ? "outlined" : "text"}
                        onClick={() => handleDayChange(week['days'][index], index)}
                        sx={{
                            minWidth: '10px', height: '40px',
                            padding: '3px 3px',
                            color: index === week['daysDayLabel'].length - 2 ? CxAppColor.saturday
                                 : index === week['daysDayLabel'].length - 1 ? CxAppColor.sunday
                                 : CxAppColor.weekday
                        }}
                    />
                ))}
            </>
        );
    };

    const renderCtrlDaysOrWeek = () => {
        const p1Week = CalendarUtil.isoWeek(week['prevStart']);
        const p2Week = CalendarUtil.isoWeek(p1Week['prevStart']);
        const n1Week = CalendarUtil.isoWeek(week['nextStart']);
        const n2Week = CalendarUtil.isoWeek(n1Week['nextStart']);

        const isDay= view === "dayView";
        const label = isDay ? 'Tag' : 'Woche';

        return (
            <CxBox label={label}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }} >
                    <CxTooltipButton label={kwWeekLabel(p1Week)}  iconName="prev"
                        onClick={() => handleWeekChange(p1Week)} />

                    {isDay ? renderCtrlDays() : renderCtrlWeek(p2Week, p1Week, n1Week, n2Week)}

                    <CxTooltipButton label={kwWeekLabel(n1Week)} iconName="next"
                        onClick={() => handleWeekChange(n1Week)} />
                </Box>
            </CxBox>
        );
    };

    const renderCtrlDate = () => {
        return (
            <CxLocalization>
                <CxDateInput
                    id={"datum"}
                    label="Datum"
                    value={ day }
                    handleChange={ handleDateChange }
                />
            </CxLocalization>
        );
    }

    const renderCtrlFilterForeman = () => {
        const name = deputy ? "Bauführer Stellvertreter" : "Bauführer";
        return (
                <CxComboxSelection
                    id="foreman"
                    name={name}
                    value={foremanFilter}
                    readOnly={!accessCtrl.canCtrlTaskList}
                    onChange={handleForemanChange}
                    items={crew.getForemanSelection(true, "Alle")}
                />
        );
    }

    const renderCtrlFilterGroup = () => {
        return (
            <CxComboxSelection
                key="groupBox"
                id="group"
                name="Gruppe / Subunternehmer"
                value={groupFilter}
                readOnly={!accessCtrl.canCtrlTaskList}
                onChange={handleGroupChange}
                items={crew.getTeamLeadGroupAndContractorSelection("Alle")}
            />
        );
    }

    const renderCtrlFilterMember = () => {
        return (
            <CxComboxSelection
                key="memberBox"
                id="member"
                name="Mitarbeiter"
                value={memberFilter}
                readOnly={!accessCtrl.canCtrlTaskList}
                onChange={handleMemberChange}
                items={crew.getTeamMemberSelection("Alle")}
            />
        );
    }

    const renderCtrlFilterTask = () => {
        return (
            <CxComboxSelection
                id="task"
                name="Arbeitskategorie"
                value={taskFilter}
                readOnly={!accessCtrl.canCtrlTaskList}
                onChange={handleTaskChange}
                items={code.getTaskSelection("Alle")}
            />
        );
    }

    const renderCtrlOptionDisplay = () => {
        const displaySelection = [
            {value:-1, label: "Gesammtansicht"},
            {value: 0, label: "Arbeitskategorien"},
            {value: 1, label: "Gruppenansicht"}
        ]
        return (
            <CxComboxSelection
                id="group"
                name="Ansicht"
                value={displayOption}
                readOnly={!accessCtrl.canCtrlTaskList}
                onChange={handleDisplayChange}
                items={displaySelection}
            />
        );
    }

    //
    // renderCtrl
    //
    const renderCtrl = () => {
        return (
            <CxGrid container spacing={2} direction="row" sx={{mb: 2}}>
                <CxGrid size="md">
                    <CxGrid size="xl" container spacing={2}>
                        <CxGrid size="lg">
                            {renderCtrlFilterForeman()}
                        </CxGrid>
                        <CxGrid size="lg">
                            {member ? renderCtrlFilterMember() : renderCtrlFilterGroup()}
                        </CxGrid>
                    </CxGrid>
                </CxGrid>
                <CxGrid size="md">
                    <CxGrid size="xl" container>
                        <CxGrid size="xl">
                            { view !== "dateView" && renderCtrlDaysOrWeek()}
                            { view === "dateView" && renderCtrlDate() }
                        </CxGrid>
                    </CxGrid>
                </CxGrid>
                <CxGrid size="md">
                    <CxGrid size="xl" container spacing={2}>
                        <CxGrid size="lg">
                            {renderCtrlFilterTask()}
                        </CxGrid>
                        <CxGrid size="lg">
                            {renderCtrlOptionDisplay()}
                        </CxGrid>
                    </CxGrid>
                </CxGrid>
            </CxGrid>
        );
    }

    // ----------------------------------------------------------------------------------------------------------------
    // load view data (taskList)
    // ----------------------------------------------------------------------------------------------------------------

    const [loads, setLoads] = useState(null);
    const [call, setCall] = useState(runningCall());

    // TODO MAINTENANCE: add active flag and useEffect response
    useEffect(() => {
        if (!code || !crew || !week) return; // avoid firing too early (guard for undefined or null)

        setCall(runningCall());
        updateAppStatus('call', pendingStatus("Task data load is pending."));

        taskListRange(week['start'], week['end']).then(responseCall => {
            if (responseCall.isSuccess) {
                const mappedRows = Task.mapListColumns(responseCall.result, code, crew);
                setCall(successCall(mappedRows));
                setLoads(Capa.processTasks(mappedRows, week.daysIso))
                updateAppStatus('call', successStatus(`Task data ${responseCall.result.length} records successfully loaded.`, responseCall.elapsed));
            } else {
                setCall(responseCall);
                updateAppStatus('call', errorStatus(`Task data load failed with error: ${responseCall.result}`, responseCall.elapsed));
            }
        });

    }, [code, crew, week]);


    // ----------------------------------------------------------------------------------------------------------------
    // filter view data
    // ----------------------------------------------------------------------------------------------------------------

    const rows = useMemo(() => {

        if (!call.isSuccess) {
            return [];
        }

        const isoDay = DateUtil.convertDateToIsoString(day);

        return call.result
            .map(task => {

                // map members from extensions for single dates (if available)
                if ( task.has_extensions && (view === "dayView" || view === "dateView") ) {
                    const day = Task.mapListColumMembers4Day(task, isoDay, crew);
                    return {
                        ...task,
                        team_member_crew_ids: day.team_member_crew_ids,
                        team_member: day.team_member
                    };
                }

                // map original for week view and entries with no extensions
                return task;
            })
            .filter((task) => {

                if (foremanFilter !== -1) {
                    if (deputy) {
                        if (task.deputy_foreman_crew_id !== foremanFilter) {
                            return false;
                        }
                    } else {
                        if (task.foreman_crew_id !== foremanFilter) {
                            return false;
                        }
                    }
                }

                if ( member && memberFilter !== -1 && task.team_lead_crew_id !== memberFilter &&
                     !JsonUtil.convertJsonStringToArray(task.team_member_crew_ids)
                         .map(Number)
                        .includes(memberFilter) ) {
                    return false;
                }

                if (!member && groupFilter !== -1 && task.team_lead_crew_id !== groupFilter) {
                    return false;
                }

                if (taskFilter !== -1 && task.task_code_key !== taskFilter) {
                    return false;
                }

                if ( (view === "dayView" || view === "dateView") &&
                     (task.start_date > isoDay || task.end_date < isoDay)) {
                    return false;
                }

                return true;
            }
        );

    }, [call, foremanFilter, groupFilter, memberFilter, taskFilter, view, deputy, member, day]);

    const rowsByTask = useMemo(() => {
        return rows.reduce((acc, row) => {
            const key = row.task_code_key || "_null_";
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(row);
            return acc;
        }, {});
    }, [rows]);

    const rowsByTeam = useMemo(() => {
        return rows.reduce((acc, row) => {
            const key = row.team_lead_crew_id || "_null_";
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(row);
            return acc;
        }, {});
    }, [rows]);

    const getTeamListGroupOrder = () => {
        return Array.from(
            new Map(
                rows.map(row => {
                    const key = row.team_lead_crew_id ?? '_null_';              // use a placeholder str for null keys
                    const label = row.team_lead ?? '_keine Gruppe_';     // display label for nulls
                    return [key, { key, label }];
                })
            ).values()
        ).sort((a, b) =>
            a.label.localeCompare(b.label)
        );
    };

    // ----------------------------------------------------------------------------------------------------------------
    // render view
    // ----------------------------------------------------------------------------------------------------------------

    const handleRowDoubleClick = (row) => {
        handleAction("projectDetailFromTask", row)
    };

    const handleEditClick = (row) => {
        handleAction("taskUpdate", row, "task");       // return back to 'task' view after edit
    }

    const handleDocClick = (row) => {
        handleAction("taskShow", row, "task");       // return back to 'task' view after edit
    }

    const renderRowsHeader = (title) => {
        return (
            <CxGrid container size="xl" alignItems="center" sx={{ gap: 1.2}}>
                <CxTooltipButton
                    label={capa ? "Einsätze ausblenden" : "Einsätze einblenden"}
                    iconName={ capa ? "teamCapaOn" : "teamCapaOff" }
                    onClick={handleCapaChange}
                    disabled={!accessCtrl.canCtrlTaskList}
                    sx = {{ minWidth: "auto", transform: "translateY(-6px)"}}
                />
                <CxSubtitle sx={{mb: 1}}>{ title }</CxSubtitle>
            </CxGrid>
        );
    }

    const renderRows = () => {
        return (
            <>
                <CxGrid size="xl">
                    { renderRowsHeader("Gesammtansicht") }
                </CxGrid>
                <CxGrid size="xl">
                    <CxDataGrid
                        rows={rows}
                        columns={Task.getListColumns(true, false)}
                        onRowDoubleClick={(params) => handleRowDoubleClick(params.row) }
                        onViewAction={ (params) => handleRowDoubleClick(params.row) }
                        onEditAction={canManageTaskInList(foremanFilter) ? (params) => handleEditClick(params.row) : undefined}
                    />
                </CxGrid>
            </>
        );
    }

    const renderRowsByTasks = () => {
        const taskOrder = code.getTaskListGroupOrder();             // retrieve the ordered list of groups
        return (
            <>
                { renderRowsHeader("Arbeitskategorien")}
                {taskOrder.map(({ key, label }) => {
                    const taskRows = rowsByTask[key];

                    // Skip rendering if no data exists for this group
                    if (!taskRows || taskRows.length === 0) return null;

                    return (
                        <CxGrid key={key} size="xl">
                            <CxGrid size="xl" sx={{ mt: 2 }}>
                                <CxSection>{label}</CxSection>
                            </CxGrid>
                            <CxGrid size="xl">
                                <CxDataTable
                                    rows={taskRows}
                                    columns={Task.getListColumns(false)}
                                    onRowDoubleClick={handleRowDoubleClick}
                                    onViewAction={handleRowDoubleClick}
                                    onEditAction={canManageTaskInList(foremanFilter) ? handleEditClick : undefined}
                                    onDocAction={handleDocClick}
                                />
                            </CxGrid>
                        </CxGrid>
                    );
                })}
            </>
        );
    };


    const renderRowsByTeams = () => {
        const teamOrder = getTeamListGroupOrder();
        return (
            <>
                { renderRowsHeader("Gruppenansicht")}
                {teamOrder.map(({ key, label }) => {
                    const taskRows = rowsByTeam[key];

                    // Skip rendering if no data exists for this group
                    if (!taskRows || taskRows.length === 0) return null;

                    return (
                        <CxGrid key={key} size="xl">
                            <CxGrid size="xl" sx={{ mt: 2 }}>
                                <CxSection>{label}</CxSection>
                            </CxGrid>
                            <CxGrid size="xl">
                                <CxDataTable
                                    rows={taskRows}
                                    columns={Task.getListTeamColumns()}
                                    onRowDoubleClick={handleRowDoubleClick}
                                    onViewAction={handleRowDoubleClick}
                                    onEditAction={canManageTaskInList(foremanFilter) ? handleEditClick : undefined}
                                    onDocAction={handleDocClick}
                                />
                            </CxGrid>
                        </CxGrid>
                    );
                })}
            </>
        );
    };

    // ----------------------------------------------------------------------------------------------------------------
    // render capa & tool capa
    // ----------------------------------------------------------------------------------------------------------------

    const showNumbers = getPageState('TaskList', 'showNumbers');          // capa showNumers or graphic

    const prepareLoads = () => {
        const replaceAndSort = (map) => {
            return new Map(
                [...map.entries()]
                    .map(([id, data]) => [crew.lookupName(id), data])
                    .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
            );
        };
        return {
            groupLoad: replaceAndSort(loads.groupLoad),
            workerLoad: replaceAndSort(loads.workerLoad),
            contractorLoad: replaceAndSort(loads.contractorLoad)
        };
    };

    const prepareToolUsage = () => {
        const getCodes = (map) => {
            return new Map([...map.entries()].map(([id, _]) => [id, code.getCode(id)?.[0] || {}])); // Extract first object
        };

        const pumpCodes = getCodes(loads.pumpUsage);

        const combinedData = [...loads.pumpUsage.entries()]
            .map(([key, loads]) => ({
                key,
                loads,
                code: pumpCodes.get(key) ?? {} // Ensure it's an object, not an array
            }))
            .sort((a, b) => (a.code.value || "").localeCompare(b.code.value || "")); // Avoid sorting undefined

        return combinedData;
    };

    const renderCapa = () => {
        return (
            <>
                <CxGrid size="xl" sx={{mb: 1}}>
                    <CxSubtitle>{Capa.getLabel(week.nr)}</CxSubtitle>
                </CxGrid>

                <CxGrid size="xl" container spacing={2}>
                    <CxComboxSelection
                        id="capaview"
                        name="Anzeige"
                        value={showNumbers ? 1 : 0}
                        onChange={handleCapaViewChange}
                        items={Capa.getCapaViewSelection()}
                    />
                </CxGrid>

                <CxGrid size="xl" container sx={{ border: "1px solid black", p: 0}}>
                    <CxCapa showNumbers={showNumbers} dataSets={prepareLoads()} />
                </CxGrid>
            </>
        );
    }

    const renderToolCapa = () => {
        return (
            <CxInfoDialog
                open={toolCapa}
                title="Auslastung Werkzeuge"
                content={<CxToolCapa dataSets={prepareToolUsage?.()} />}
                handleInfo={handleInfoChange}
            />
        );
    }

    const reportInfo = () => {
        const parts = [];
        if (!deputy && foremanFilter !== -1) parts.push(`Bauführer: ${ crew.getName(foremanFilter)}`);
        if (deputy && foremanFilter  !== -1) parts.push(`Bauführer Stellvertreter: ${ crew.getName(foremanFilter)}`);
        if (!member && groupFilter   !== -1) parts.push(`Gruppe: ${crew.getName(groupFilter)}`);
        if ( member && memberFilter  !== -1) parts.push(`Mitarbeiter: ${crew.getName(memberFilter)}`);
        if (taskFilter               !== -1) parts.push(`Arbeitsvorgang: ${code.getValue(taskFilter)}`);
        return parts.length > 0 ? `${parts.join(', ')}` : null;
    }

    const renderReport = () => {
        const viewLabel = view === "weekView" ? kwWeekLabel(week) : dayLabel(day, dayIndex);
        const reportTitle = Report.getName(report) + " " + viewLabel;
        const reportRows = Report.getReportRows(report, code, rows);
        const reportGroupBy = Report.getGroupBy(report);

        return (
            <>
                { reportGroupBy === null &&
                    <CxReportDialog reportType={report} title={reportTitle} rows={reportRows}
                                    open={!!report} onClose={() => setReport(null)} info={reportInfo()}
                    />
                }
                { reportGroupBy !== null &&
                    <CxGroupReportDialog reportType={report} title={reportTitle} rows={reportRows} groupBy={reportGroupBy}
                                         open={!!report} onClose={() => setReport(null)} info={reportInfo()}
                    />
                }
            </>
        )
    }

    // ----------------------------------------------------------------------------------------------------------------
    // render task list
    // ----------------------------------------------------------------------------------------------------------------

    return (

        <Box sx={{width: '100%'}}>

            <Box sx={{ width: "100%", mb: 0 }}>
                {renderHeader()}
                {renderSubHeader()}
                {renderCtrl()}
                <CxListFeedback call={call} />
            </Box>

            { call.isSuccess && (
                <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>

                    { capa && (
                        <CxGrid sx={{ minWidth: "200px", flexShrink: 0 }}>
                            { renderCapa() }
                        </CxGrid>
                    )}

                    { toolCapa && renderToolCapa() }
                    { report   && renderReport() }

                    <CxGrid container spacing={2} sx={{ flexGrow: 1, overflow: "hidden", display: "flex", flexDirection: "column", width: "100%" }}>
                        {displayOption === -1 && renderRows() }
                        {displayOption ===  0 && renderRowsByTasks() }
                        {displayOption ===  1 && renderRowsByTeams() }
                    </CxGrid>

                </Stack>
            )}

        </Box>

    );
}

// --------------------------------------------------------------------------------------------------------------------
// The end.